// @ts-strict-ignore
import React, { useMemo, useState } from 'react';
import {
  Banner,
  Button,
  ButtonVariant,
  Box,
  Modal,
  useSnackBar,
} from '@usealloy/component-library';

import { ajaxFetch } from '~/helpers/api';
import { getRoute } from '~/helpers/routes';
import {
  ModalBodyWrapper,
  ModalContentWrapper,
  ModalTabWrapper,
} from './styles';
import { initializeTabCounts } from './helpers';
import MetadataTabs from './MetadataTabs';
import MetadataContent from './MetadataContent';

interface MetadataTemplateModalProps {
  closeModal: () => void;
  data: object;
  evaluationApplicationId: string;
  initialWhitelist: object;
  isOpen: boolean;
  setState: (any) => void;
}

const MetadataTemplateModal = ({
  closeModal,
  data,
  evaluationApplicationId,
  initialWhitelist,
  isOpen,
  setState,
}: MetadataTemplateModalProps) => {
  const addSnackBar = useSnackBar();
  const tabs = useMemo(() => (
    Object.keys(data || {}).filter(key => typeof data[key] === 'object' && data[key] !== null)
  ), [data]);
  const initialTabCounts = useMemo(() => (
    initializeTabCounts(tabs, initialWhitelist, data)
  ), [tabs, initialWhitelist, data]);
  const [currentTab, setCurrentTab] = useState(tabs[0]);
  const [whitelist, setWhitelist] = useState(initialWhitelist);
  const [modalError, setModalError] = useState(null);
  const [tabCounts, setTabCounts] = useState(initialTabCounts);

  const handleClose = () => { setModalError(null); closeModal(); };

  const saveMetadataTemplate = async () => {
    if (modalError) setModalError(null);
    const response = await ajaxFetch(getRoute('evaluations.metadataTemplates', null), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        application_id: evaluationApplicationId,
        whitelist,
      }),
      createMessage: ([message]) => { setModalError(message); },
    });
    if (response.result === 'success') {
      addSnackBar({ title: 'Template updated.', type: 'success' });
      setState(s => ({
        ...s,
        creatingTemplate: false,
        template: response.template,
        whitelist: response.whitelist,
      }));
      closeModal();
    }
  };

  const SaveButton = (
    <Button
      onClick={() => {
        saveMetadataTemplate();
      }}
      tabIndex={0}
      variant={ButtonVariant.primary}
    >
      Save
    </Button>
  );

  const CancelButton = (
    <Button
      onClick={handleClose}
      tabIndex={0}
      variant={ButtonVariant.secondary}
    >
      Cancel
    </Button>
  );

  return (
    <Modal
      hasCloseIcon
      header="Configure Other Data"
      isOpen={isOpen}
      onClose={handleClose}
      primaryAction={SaveButton}
      secondaryAction={CancelButton}
      size="large"
    >
      <Box direction="column" justifyContent="flex-start" alignItems="flex-start" gap={4}>
        <Banner
          onDismiss={() => { setModalError(null); }}
          open={modalError}
          variant="error"
        >
          {modalError}
        </Banner>
        <ModalBodyWrapper gap={5} justifyContent="space-around">
          <ModalTabWrapper
            alignItems="flex-start"
            gap={4}
            justifyContent="flex-start"
          >
            <MetadataTabs
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              tabCounts={tabCounts}
              tabs={tabs}
            />
          </ModalTabWrapper>
          <ModalContentWrapper
            alignItems="flex-start"
            gap={2}
            justifyContent="flex-start"
          >
            <MetadataContent
              data={data?.[currentTab] || {}}
              currentTab={currentTab}
              setTabCount={operator => { // operator is a callback function to update the count, e.g. value => value + 1
                setTabCounts({ ...tabCounts, [currentTab]: operator(tabCounts[currentTab]) });
              }}
              setWhitelist={value => { setWhitelist({ ...whitelist, [currentTab]: value }); }}
              whitelist={whitelist?.[currentTab] || {}}
            />
          </ModalContentWrapper>
        </ModalBodyWrapper>
      </Box>
    </Modal>
  );
};

export default MetadataTemplateModal;
